// Define the Option type used in the LOVs
type Option = { value?: string; disabled?: boolean; [key: string]: unknown };

export const TIM_TYPE_FIELD_NAME = 'current_party_location_status_type_desc';
export const TIM_REQUIREMENT_ORIGIN_FIELD_NAME = 'requirement_origin_type_desc';

export const TIM_RO_INTERNAL_MARKET_MOVE = 'Internal Market Move';
export const TIM_RO_NEW_TO_MARKET = 'New to Market';

// Map must match the one used by TimTypePluginService (kept inline to avoid cross-file coupling)
// The key is the requirement origin in lowercase, and the value is the list of allowed TIM types
export const TIM_TYPE_LOV_MAP: Record<string, string[]> = {
  [TIM_RO_INTERNAL_MARKET_MOVE]: ['Renewal/Relocation', 'Growth', 'Out of Flex Space', 'Unknown'],
  [TIM_RO_NEW_TO_MARKET]: ['Newly Created Company', 'Foreign Company', 'Existing Company', 'Unknown'],
};

// Utility function to check for empty strings or null/undefined
const isEmpty = (val: string): boolean => val == null || val === '';

// Utility function to check if a given TIM type value is valid for a requirement origin
export const isTimTypeLovValid = (requirementOrigin: string | undefined, timTypeValue: string): boolean => {
  const timTypeKey = requirementOrigin ?? '';
  if (isEmpty(requirementOrigin) || isEmpty(timTypeValue)) {
    return true;
  }

  // If there is no restriction mapping for the requirement origin, treat as valid
  if (TIM_TYPE_LOV_MAP[timTypeKey] == null) {
    return true;
  }

  const allowed = TIM_TYPE_LOV_MAP[timTypeKey];
  return allowed.includes(timTypeValue as string);
};

const formatLOVOption = (option: Option, timTypeKey: string): Option => ({
  ...option,
  disabled: !isTimTypeLovValid(timTypeKey, option.value),
});

// Function to get LOVs formatted with disabled flags based on requirement origin
export const getLOVsFormatted = (requirementOrigin: string | undefined, LOVOptions: { value?: string }[]) => {
  const timTypeKey = requirementOrigin ?? '';
  const formattedOptions = LOVOptions.map(o => formatLOVOption(o, timTypeKey));
  return formattedOptions;
};

export type RequirementOriginResult = {
  value: string | null;
  apply: boolean;
};

const SKIP_APPLY_AUTOPOPULATE = ['Unknown', null, undefined, ''];

export const getRequirementOriginBaseOnTimType = (timTypeValue: string): RequirementOriginResult => {
  // Only Auto-Populate if TIM Type is not 'Unknown'
  if (SKIP_APPLY_AUTOPOPULATE.includes(timTypeValue)) {
    return { value: null, apply: false };
  }
  // Determine the requirement origin by finding a key in TIM_TYPE_LOV_MAP
  // whose allowed list contains the new TIM type. Keys in TIM_TYPE_LOV_MAP
  // are stored lowercase in the source file; allow case-insensitive matching.
  const foundKey = Object.keys(TIM_TYPE_LOV_MAP).find(k => {
    const allowed = TIM_TYPE_LOV_MAP[k] || [];
    return allowed.includes(String(timTypeValue));
  });

  // If no mapping found, overwrite existing requirement origin with null
  // means the current value is invalid based on TIM type selected
  if (!foundKey) {
    return { value: null, apply: true };
  }

  return { value: foundKey, apply: true };
};
